import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import { Breadcrumbs, FAQ, FinalCTA, GuideByline } from "../../components/seo/SEOBlocks";
import { CONTENT_LAST_UPDATED_ISO } from "../../lib/seoRegistry";

const CRUMBS = [
  { name: "Home", href: "/" },
  { name: "Guides", href: "/guides/prototype-vs-poc-vs-mvp" },
  { name: "Prototype vs POC vs MVP", href: "/guides/prototype-vs-poc-vs-mvp" },
];

const FAQS = [
  { q: "What's the shortest possible definition?", a: "A prototype tests a design. A proof of concept tests a technical assumption. An MVP tests a market. They look similar from the outside but they're built to answer completely different questions." },
  { q: "Which one do I need to raise a pre-seed round?", a: "Almost always an MVP. Investors at pre-seed are looking for evidence you can execute AND that users respond — that means live software with real user flows, not a clickable prototype." },
  { q: "Can I skip prototypes and go straight to MVP?", a: "Yes, if you already have enough conviction on the design direction. Prototypes are a de-risking tool for design uncertainty. If your design is a well-understood pattern (e.g. a familiar SaaS dashboard), skipping the prototype phase saves time." },
  { q: "When is a proof of concept useful?", a: "When your idea depends on one specific technical bet — an AI model reliably doing X, an integration behaving in a particular way, a performance target being achievable. Prove the bet with a POC before you invest in the full MVP." },
  { q: "Is a Figma file a prototype?", a: "It can be. If it demonstrates the intended user flow and can be tested with users, yes. If it's just static screens with no navigation, that's a wireframe or a mockup — closer to a document than a prototype." },
];

export default function GuidePrototypeVsPocVsMVP() {
  return (
    <div className="min-h-screen bg-white" data-testid="page-guide-terminology">
      <Navbar />
      <section className="pt-32 pb-8 md:pt-40 md:pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Breadcrumbs items={CRUMBS} />
          <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Guide</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-zinc-950 leading-[1.05]">
            Prototype vs proof of concept vs MVP — what's the difference?
          </h1>
          <p className="mt-6 text-lg text-zinc-600 leading-relaxed">
            The three terms get used interchangeably in casual conversation. They shouldn't be. Each one tests a different question, costs a different amount, and takes a different amount of time. Getting the difference right can save weeks and thousands.
          </p>
          <GuideByline date={CONTENT_LAST_UPDATED_ISO} />
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 prose prose-zinc prose-headings:tracking-tight prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:font-bold prose-p:leading-relaxed prose-p:text-zinc-700 prose-a:text-zinc-900 prose-a:underline">
        <h2>Prototype — tests the design</h2>
        <p>
          A prototype is a clickable, interactive artefact that simulates the intended user experience. It exists to answer one question: <em>does the design work?</em> Do users navigate it the way we expect? Do they find the primary action? Do they get stuck?
        </p>
        <p>
          A prototype does <strong>not</strong> have a real backend. There's no database, no auth, no persistence between sessions. Data is faked, buttons trigger simulated responses, and everything resets when you refresh. This is a feature, not a bug — the point is to iterate on design at ten times the speed of iterating on real software.
        </p>
        <p>
          Typical tools: Figma with prototyping enabled, ProtoPie, Framer. Typical time: hours to a few days. Typical cost: free-to-low.
        </p>

        <h2>Proof of concept (POC) — tests a technical bet</h2>
        <p>
          A proof of concept exists to answer a specific technical question that could kill the project. Common examples: <em>Can this AI model actually classify what I need it to?</em> <em>Can this third-party API deliver responses fast enough?</em> <em>Can our stack handle the load pattern we expect?</em>
        </p>
        <p>
          A POC is real code, usually messy, focused entirely on the risky bet. It has no user-facing polish — the audience is you and your co-founder, not future users. Once you have the answer, you throw the code away and start the MVP on a clean foundation.
        </p>
        <p>
          Typical time: days to a couple of weeks depending on the technical question. Typical cost: developer time for the specific investigation, nothing more.
        </p>

        <h2>MVP — tests the market</h2>
        <p>
          A Minimum Viable Product is real, running software that a real user can sign up for and complete the core value action inside. It exists to answer the biggest question of them all: <em>does anyone actually want this?</em>
        </p>
        <p>
          Unlike a prototype, an MVP has a real backend, real data persistence, real auth. Unlike a POC, it has enough polish that a stranger can use it without a tutorial. It is not a finished product — it is deliberately narrow, doing one thing that delivers the core value, and nothing else.
        </p>
        <p>
          Typical time: one to four weeks with AI-assisted delivery, up to six months with a traditional agency. Typical cost: see our <Link to="/guides/mvp-development-cost">MVP development cost guide</Link>.
        </p>

        <h2>Quick decision framework</h2>
        <ul>
          <li><strong>Not sure about the design?</strong> Build a prototype first. Cheap and fast to iterate.</li>
          <li><strong>Not sure the tech will work?</strong> Build a proof of concept before the MVP. Answer the technical question in isolation.</li>
          <li><strong>Sure about both, but not sure users care?</strong> Skip straight to the MVP. Everything else is procrastination.</li>
          <li><strong>Sure of everything?</strong> Congratulations — you don't need to be reading this. Ship it.</li>
        </ul>

        <h2>What FastPrototype builds</h2>
        <p>
          Despite the name, we build MVPs — real, running software you can put in front of real users. The "prototype" in our name is a nod to the speed, not the fidelity. If you're at the point where you need a clickable prototype, a tool like Figma will serve you better than us. If you're past that point and need actual working software, <Link to="/build">start a scope</Link>.
        </p>
      </article>

      <FAQ items={FAQS} title="Terminology — quick answers" />
      <FinalCTA heading="Past the design phase?" subheading="If you're ready for real software, we deliver in one to four weeks." buttonLabel="Start an MVP scope" />
      <Footer />
    </div>
  );
}
